import debug from 'debug';
import numeral from 'numeral';
import { sprintf } from 'sprintf-js';
import * as _ from 'lodash';
import { uniqueID } from './unique-id';
import { parseISO, format, add } from 'date-fns';
import * as dateFns from 'date-fns';
import humanizeDuration from 'humanize-duration';
import { PagingDataSourceImpl, createSearchDataSource, CRUD, parseConnectionString, createSlickDataSource } from './connections';
import { asFormData } from './connections/helpers';
import { toYamlString, imageData } from './helpers';
import { Resource } from './resource';
import { Poller } from './poller';
import { unaccent } from '@integro/gettext';
import { ResourceProxy } from './resource-proxy';
import { CollectionBasedPagingDataSource, Pager } from './pagination';
import * as downloads from './downloads';
const log = debug('itp:core:fn');
export function fns(app) {
    return {
        dateFns,
        _: _.default,
        sprintf,
        asFormData,
        toYamlString,
        parseISODate: parseISO,
        formatDate: format,
        today() {
            return format(new Date(), 'yyyy-MM-dd');
        },
        dateAdd(d, duration) {
            return format(add(parseISO(d), duration), 'yyyy-MM-dd');
        },
        epoch(argument) {
            return parseISO(argument).valueOf();
        },
        isWeekend(d) {
            if (_.isString(d)) {
                d = parseISO(d);
            }
            if (_.isDate(d)) {
                const dayOfWeek = d.getDay();
                return dayOfWeek === 6 || dayOfWeek === 0;
            }
            return false;
        },
        gettext: app.i18n.$$translator.gettext.bind(app.i18n.$$translator),
        ngettext: app.i18n.$$translator.ngettext.bind(app.i18n.$$translator),
        pgettext: app.i18n.$$translator.pgettext.bind(app.i18n.$$translator),
        npgettext: app.i18n.$$translator.npgettext.bind(app.i18n.$$translator),
        crud(objectType) {
            return CRUD.createCRUD(objectType, app);
        },
        fetch(operation, options) {
            if (_.isString(operation)) {
                options = options || { asResource: false, asResourceContainer: false };
                const isBase64ImageDataRequest = options.responseConverter === 'base64imagedata';
                if (isBase64ImageDataRequest) {
                    options.responseType = 'arraybuffer';
                }
                const cn = parseConnectionString(operation);
                const conn = app.connections.getConnection(cn.connection);
                return conn
                    .request(operation, options)
                    .then(response => onResponse(response));
                function onResponse(response) {
                    if (response.status === 204) {
                        return null;
                    }
                    if (response.status === 200 && isBase64ImageDataRequest) {
                        return imageData(response);
                    }
                    if (options.asResource && _.isArray(response.data)) {
                        const res = _.map(response.data, p => new Resource(p));
                        log('fetch-resource %o', res);
                        return res;
                    }
                    if (options.asResource && _.isObject(response.data)) {
                        const res = new Resource(response.data);
                        log('fetch-resource %o', res);
                        return res;
                    }
                    if (options.asResourceContainer && _.isArray(response.data)) {
                        const res = {
                            object: 'list',
                            self: response.config.url,
                            data: _.map(response.data, p => new Resource(p))
                        };
                        log('fetch-resource-container %o', res);
                        return res;
                    }
                    return response.data;
                }
            }
            const conn = app.connections.default;
            const url = `${conn.config.baseUrl}${operation.url}`;
            return app.connections.ajax(url);
        },
        schemaDefaults(name, extras, asResource = true) {
            const d = _.extend({
                id: null,
                links: { self: null }
            }, app.connections.default.getSchemaDefaults(name), extras);
            return asResource ? new Resource(d) : d;
        },
        getSchema(name) {
            return app.connections.default.getSchema(name);
        },
        getEnumValuesFromSchema(typeName, options) {
            const schema = app.connections.default.getSchema(typeName);
            const sort = options && options.sort != null ? options.sort : true;
            if (schema && schema.enum) {
                const result = _.chain(schema.enum).map(p => ({
                    value: p,
                    text: app.i18n.translate(p, {
                        context: typeName,
                        conversion: 'Sentence'
                    })
                }));
                if (sort) {
                    return result.sortBy(p => unaccent(p.text).toLowerCase()).value();
                }
                return result.value();
            }
        },
        pagingDataSource(operation) {
            return new PagingDataSourceImpl(app.connections.default, operation);
        },
        collectionBasedPagingDatasource(collection, pageSize) {
            return new CollectionBasedPagingDataSource(collection, pageSize);
        },
        pager(collection, pageSize) {
            return new Pager(collection, pageSize);
        },
        searchDataSource(operation, parameters) {
            return createSearchDataSource(app.connections.default, operation, parameters);
        },
        slickDataSource(name, options) {
            return createSlickDataSource(app, options ? _.extend(options, { name }) : { name });
        },
        randomElementId() {
            return uniqueID();
        },
        newResourceID(prefix) {
            return prefix ? `${prefix}${uniqueID()}` : uniqueID();
        },
        Resource,
        urlFor: app.connections.urlFor.bind(app.connections),
        poller(options) {
            return new Poller(options);
        },
        resourceProxy(options) {
            return new ResourceProxy(app, options);
        },
        imageData,
        numeral,
        humanizeDuration(duration) {
            return humanizeDuration(duration, {
                language: app.i18n.locale,
                maxDecimalPoints: 2
            });
        },
        showFileInNewTab(url, fileName) {
            return Promise.resolve(downloads.getFileBLOB(url, fileName)).then(p => p && downloads.showFileInNewTab(p.file, p.fileName));
        }
    };
}
//# sourceMappingURL=fn.js.map